import { useCallback, useContext, useEffect, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { View } from '@/components/Themed';
import Head from 'expo-router/head';
import * as AppUtils from '@/utilities/AppUtils';
import * as AppUtilities from '@/utilities/AppUtilities';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import { Button, Hourglass, Panel } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setLoggedIn, removeSearchResultByID } from '@/state/user';
import { ExternalPathString, Link, RelativePathString, router, useRouter } from 'expo-router';
import { useLocalSearchParams } from 'expo-router';
import Footer from '@/components/Footer';
import * as AppConstants from '@/utilities/AppConstants';
import { AppContext } from '@/components/AppContext';
import { Collaboration, UNKNOWN_COLLABORATION } from '@/types/collaboration';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  image: {
    width: 200,
    height: 200,
  },
  bottomLeft: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    backgroundColor: 'transparent',
  },
  bottomRight: {
    position: 'absolute',
    bottom: 30,
    right: 30,
    backgroundColor: 'transparent',
  },
  imageWrapper: {
    backgroundColor: 'transparent',
  },
  wrapper: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  decisionImage: {
    width: 64,
    height: 64,
  },
  decisionWrapper: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    width: '100%',
    gap: 40,
    position: 'absolute',
    bottom: 0,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 34,
    backgroundColor: 'transparent',
  },
  largeAvatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: 'transparent',
  },
  header: {
    padding: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
    gap: 20,
  },
  emptyContainer: {
    padding: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
    gap: 20,
  },
  sendButton: {
    marginRight: 2,
    marginBottom: 2,
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  content: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  flexRowGapVerticalCenter: {
    flexDirection: 'row',
    gap: 10,
    alignItems: "center",
    backgroundColor: "transparent",
  },
  flexRowGap: {
    flexDirection: 'row',
    gap: 10,
    alignItems: "center",
    backgroundColor: "transparent",
  },
  editImage: {
    width: 32,
    height: 32,
  },
  editImageWrapper: {
    position: 'absolute',
    right: 0,
    bottom: 0,
  },
});

export default function RabblCollabAdmin() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const { manage_community_id } = useLocalSearchParams<{ manage_community_id: string }>();
  const { manage_collab_id } = useLocalSearchParams<{ manage_collab_id: string }>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const router = useRouter();
  const [collaboration, setCollaboration] = useState(UNKNOWN_COLLABORATION);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      AppUtilities.info("RabblCollabAdmin.loadInitialData started");
      const response = await AppUtilities.fetchAuthorized(
        "RabblCollabAdmin.loadInitialData",
        "/rabbl/api/admincollab",
        "POST",
        {
          collaboration_uniqueid: manage_collab_id,
          community_uniqueid: manage_community_id,
        }
      );
      AppUtilities.info("RabblCollabAdmin.loadInitialData finished:");
      AppUtilities.dir((response as any).body);

      const collab: Collaboration = ((response as any).body as any).collaboration;
      setCollaboration(collab);

    } catch (e) {
      AppUtilities.handleException(e, "RabblCollabAdmin.loadInitialData");
      setError(AppUtilities.getErrors(e));
    } finally {
      setLoading(false);
    }
  }, [manage_collab_id, manage_community_id]);

  useEffect(() => {
    if (manage_collab_id) {
      loadInitialData();
    }
  }, [manage_collab_id]);

  if (loading || !AppUtils.isValidCollaboration(collaboration)) {
    return (
      <View style={AppConstants.STYLES.container2}>
        <Hourglass size={AppConstants.LARGE_HOURGLASS_SIZE} />
      </View>
    );
  }

  if (error.length > 0) {
    return (
      <View style={AppConstants.STYLES.container2}>
        <CustomText>Failed to load</CustomText>
        <AppText>{error}</AppText>
        <Button onPress={loadInitialData}>Retry</Button>
      </View>
    );
  }

  return (
    <BasicScrollView center={true}>
      <Meta title={"Manage collab"} />
      
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <CustomText>Collaboration</CustomText>
          <AppText>TODO</AppText>
        </View>
      </Panel>
        
      <Footer />
    </BasicScrollView>
  );
}
